import React, {useEffect, useState} from 'react';
import TopNavBar from '@app/components/TopNavBar';
import useMediaQuery from '@mui/material/useMediaQuery';
import {Box, CssBaseline} from '@mui/material';
import {ErrorBoundary} from '@sentry/react';
import {Helmet} from 'react-helmet';
import {ThemeProvider} from '@mui/material/styles';
import {useAuth} from '@apps-common/auth-react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';

import AppContent from './App.jsx';
import ErrorPage from '@app/components/common/ErrorPage';
import {API_TARGET, MAPBOX_ACCESS_TOKEN} from '@app/config';
import {MapAuthProvider} from '@apps-common/ui-maps/context';
import {THEME_MODE, darkTheme, lightTheme} from '../styles/index.jsx';

const AppLayout = () => {
  const {t} = useTranslation();
  const {user} = useAuth();

  const themeModeLabOn = useSelector(state => state.labs.themeMode);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const systemThemePref = prefersDarkMode ? THEME_MODE.DARK : THEME_MODE.LIGHT;
  const [themeSwitch, setThemeSwitch] = useState(
    localStorage.getItem('pl-theme-mode') || THEME_MODE.LIGHT
  );

  useEffect(() => {
    if (themeModeLabOn && localStorage.getItem('pl-theme-mode') === null) {
      setThemeSwitch(THEME_MODE.SYSTEM);
    } else {
      setThemeSwitch(localStorage.getItem('pl-theme-mode') || THEME_MODE.LIGHT);
    }
  }, [setThemeSwitch, themeModeLabOn]);

  const themeMode = themeModeLabOn
    ? themeSwitch === THEME_MODE.SYSTEM
      ? systemThemePref
      : themeSwitch
    : THEME_MODE.LIGHT;

  return (
    <ThemeProvider
      theme={themeMode === THEME_MODE.DARK ? darkTheme : lightTheme}
    >
      <MapAuthProvider
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        authToken={user.accessToken}
        authTarget={API_TARGET}
      >
        <Helmet>
          <title>{t('common:meta.name')}</title>
          <meta itemProp="name" content={t('common:meta.name')} />
          <meta property="og:title" content={t('common:meta.name')} />
          <meta property="og:description" content={t('common:meta.tagline')} />
          <meta property="og:url" content={t('common:meta.url')} />
        </Helmet>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }}
        >
          <ErrorBoundary
            fallback={errorProps => (
              <ErrorPage sentryEventId={errorProps.eventId} />
            )}
            showDialog
            dialogOptions={{
              user: {
                name: user.claims.name,
                email: user.claims.email,
              },
            }}
          >
            <TopNavBar
              themeSwitch={themeSwitch}
              setThemeSwitch={setThemeSwitch}
            />
            <AppContent />
          </ErrorBoundary>
        </Box>
      </MapAuthProvider>
    </ThemeProvider>
  );
};

export default AppLayout;
